import * as React from 'react'
import { Platform, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Button from '@components/button/Button'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import IncidentListSection from '@components/sections/IncidentListSection'

import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'
import * as FiltreStore from '@store/filtre/filtre'
import { emptyFilters } from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { EtatWorkflowIncidentEnum } from '@api/enum'

const HOTLINE_EN_COURS = [EtatWorkflowIncidentEnum.EN_COURS_DE_TRAITEMENT]

const HOTLINE_EN_ATTENTE = [
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION,
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_PRISE_EN_CHARGE,
  EtatWorkflowIncidentEnum.REFUS_PRESTATAIRE,
]

const IncidentsHotlineHomeSection = () => {
  const i18n = useI18n()
  const siteStats = useReducer(SiteStore.store, (s) => s.siteStats)
  const navigation = useNavigation()

  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  if (!siteStats) {
    return <MissingInfo>{i18n.t('common.error')}</MissingInfo>
  }

  const displayedIncidents = React.useMemo(
    () =>
      incidents.filter(
        (inc) =>
          inc.status === EtatWorkflowIncidentEnum.REFUS_PRESTATAIRE ||
          inc.status === EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION
      ),
    [incidents]
  )

  const seeAllIncidentsButton = {
    label: i18n.t('screens.site.allIncidents'),
    onClick: () => navigation.push('/incidents'),
  }

  const displayedStat = (key: keyof IncidentsStatistiquesResponse): number | string =>
    siteStats[key] !== null && siteStats[key] !== undefined ? siteStats[key]! : i18n.t('errors.NC')

  return (
    <>
      <SectionTitle
        title={i18n.t('screens.site.incidentTotal', { count: siteStats.incidentNonResolu || 0 })}
        button={!isPhone ? seeAllIncidentsButton : undefined}
      />
      <IncidentIndicators>
        <LeftIndicators>
          <IndicatorCard inRow isLast>
            <IndicatorValue numberOfLines={3} ellipsizeMode="tail" isSmall={isPhone}>
              {siteStats.delaiInterventionMinute || siteStats.delaiInterventionMinute === 0
                ? `${Math.floor(siteStats.delaiInterventionMinute / 60)}h${siteStats.delaiInterventionMinute % 60}`
                : i18n.t('errors.NC')}
            </IndicatorValue>
            <IndicatorLabel numberOfLines={3} ellipsizeMode="tail">
              {i18n.t('screens.site.interventionDuration')}
            </IndicatorLabel>
          </IndicatorCard>
        </LeftIndicators>

        <RightIndicators>
          <IndicatorTouchCard
            activeOpacity={0.9}
            onPress={() => {
              FiltreStore.actions.setIncidentFilter(emptyFilters)
              navigation.push('/incidents')
            }}>
            <IndicatorValue>{displayedStat('incidentNonResolu')}</IndicatorValue>
            <IndicatorLabel numberOfLines={3} ellipsizeMode="tail">
              {i18n.t('screens.site.unresolvedIncidents', { count: siteStats.incidentNonResolu })}
            </IndicatorLabel>
          </IndicatorTouchCard>
          <SmallCards>
            <SmallCard
              activeOpacity={0.9}
              onPress={() => {
                FiltreStore.actions.setIncidentFilter({ ...emptyFilters, status: HOTLINE_EN_COURS })
                navigation.push('/incidents')
              }}>
              <IndicatorValue isSmall inRow>
                {displayedStat('enCours')}
              </IndicatorValue>
              <IndicatorLabel numberOfLines={3} ellipsizeMode="tail" isSmall inRow>
                {i18n.t('screens.site.inProgressIncident', { count: siteStats.enCours })}
              </IndicatorLabel>
            </SmallCard>
            <SmallCard
              activeOpacity={0.9}
              onPress={() => {
                FiltreStore.actions.setIncidentFilter({ ...emptyFilters, status: HOTLINE_EN_ATTENTE })
                navigation.push('/incidents')
              }}
              isLast>
              <IndicatorValue isSmall inRow>
                {displayedStat('enAttente')}
              </IndicatorValue>
              <IndicatorLabel numberOfLines={3} ellipsizeMode="tail" isSmall inRow>
                {i18n.t('screens.site.waitingIncident', { count: siteStats.enAttente })}
              </IndicatorLabel>
            </SmallCard>
          </SmallCards>
        </RightIndicators>
        {isPhone && (
          <ButtonContainer>
            <Button
              status="active"
              color="light"
              label={seeAllIncidentsButton.label}
              onClick={seeAllIncidentsButton.onClick}
            />
          </ButtonContainer>
        )}
      </IncidentIndicators>
      <IncidentListSection incidents={displayedIncidents} showHotlineHomeVersion />
    </>
  )
}

export default IncidentsHotlineHomeSection

const IncidentIndicators = styled(View)`
  margin: 16px 0;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint === 'phone' && `margin-bottom: 24px;`}
`
const LeftIndicators = styled(View)`
  flex-direction: row;
  ${(props) => (props.theme.breakpoint == 'phone' ? 'margin-bottom: 8px;' : 'margin-right: 12px; flex: 1;')}
`
const RightIndicators = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-left: 12px; flex: 1; ;'}
  flex-direction: row;
  align-items: center;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '16px 24px')};
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.incident};
`
const IndicatorCard = styled(View)<{ inRow?: boolean; isLast?: boolean }>`
  flex: 1;
  padding: 0 8px;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' && props.inRow ? 'row' : 'column')};
  min-height: ${(props) => (props.theme.breakpoint === 'phone' ? 60 : 80)}px;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.incident};
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.inRow && `margin-${props.isLast ? 'left' : 'right'}: ${props.theme.breakpoint === 'phone' ? 4 : 1}px;`}
`
const IndicatorTouchCard = styled(TouchableOpacity)<{ inRow?: boolean; isLast?: boolean }>`
  flex: 1;
  padding: 0 8px;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' && props.inRow ? 'row' : 'column')};
  min-height: ${(props) => (props.theme.breakpoint === 'phone' ? 60 : 80)}px;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.incident};
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.inRow && `margin-${props.isLast ? 'left' : 'right'}: ${props.theme.breakpoint === 'phone' ? 4 : 1}px;`}
  ${(props) => !props.inRow && `margin-right: 16px;`}
`

const IndicatorValue = styled(Text)<{ isSmall?: boolean; inRow?: boolean }>`
  ${(props) => props.theme.fonts.Title3};
  font-size: ${(props) => (props.isSmall ? 15 : 24)}px;
  ${(props) => props.theme.breakpoint === 'phone' && `margin: 0px 15px; min-width: 30px;`}
  ${(props) => props.inRow && `margin-right: 8px;`}
`
const IndicatorLabel = styled(Text)<{ isSmall?: boolean; inRow?: boolean }>`
  ${(props) => props.theme.breakpoint === 'phone' && props.isSmall && `flex: 1;`}
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  ${Platform.OS === 'web' && `line-height: 15px;`}
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: ${(props) => (props.theme.breakpoint === 'phone' || props.inRow ? 'left' : 'center')};
  ${Platform.OS === 'web' && 'word-break: break-word;'}
`
const SmallCards = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`};
  flex: ${(props) => (props.theme.breakpoint === 'phone' ? 1 : 2)};
`
const SmallCard = styled(TouchableOpacity)<{ isLast?: boolean }>`
  ${(props) => props.theme.breakpoint !== 'phone' && `flex: 1;`}
  min-width: 110px;
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
  border-radius: 6px;
  ${(props) => props.theme.breakpoint !== 'phone' && `margin: 4px 8px;`}
  ${(props) => props.isLast && props.theme.breakpoint === 'phone' && `margin-top: 8px`}
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.cardSecondary : props.theme.colors.background};
`
const ButtonContainer = styled(View)`
  margin-top: 16px;
`
const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
